import React from 'react';
import { Bot } from 'lucide-react';

import hubspotIcon from 'assets/images/composio/hubspot.png';
import outlookIcon from 'assets/images/composio/outlook.png';
import onedriveIcon from 'assets/images/onedrive.svg';
import sharepointIcon from 'assets/images/sharepoint.svg';
import gdriveIcon from 'assets/images/gdrive.svg';
import githubIcon from 'assets/images/social_media_github.svg';

const iconBaseClass = 'h-3.5 w-3.5 shrink-0';

export function SlackIcon({ className = iconBaseClass, ...props }: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true" {...props}>
      <path d="M5.04 14.28a2.16 2.16 0 1 1-2.16-2.16h2.16v2.16zm1.08 0a2.16 2.16 0 0 1 4.32 0v5.4a2.16 2.16 0 1 1-4.32 0v-5.4z" fill="#E01E5A" />
      <path d="M9.72 5.04a2.16 2.16 0 1 1 2.16-2.16v2.16H9.72zm0 1.08a2.16 2.16 0 0 1 0 4.32H4.32a2.16 2.16 0 1 1 0-4.32h5.4z" fill="#36C5F0" />
      <path d="M18.96 9.72a2.16 2.16 0 1 1 2.16 2.16h-2.16V9.72zm-1.08 0a2.16 2.16 0 0 1-4.32 0V4.32a2.16 2.16 0 1 1 4.32 0v5.4z" fill="#2EB67D" />
      <path d="M14.28 18.96a2.16 2.16 0 1 1-2.16 2.16v-2.16h2.16zm0-1.08a2.16 2.16 0 0 1 0-4.32h5.4a2.16 2.16 0 1 1 0 4.32h-5.4z" fill="#ECB22E" />
    </svg>
  );
}

export function TeamsIcon({ className = iconBaseClass, ...props }: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true" {...props}>
      <path d="M16.5 7.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm3 2h-4.2a1.8 1.8 0 0 0-1.8 1.8v2.95c.58.4 1.25.68 2 .8V18h4v-6.7a1.8 1.8 0 0 0-1.8-1.8h-1.8z" fill="#7B83EB" />
      <circle cx="9.5" cy="5" r="2.5" fill="#5059C9" />
      <rect x="2" y="9" width="15" height="12" rx="2" fill="#5059C9" />
      <text x="9.5" y="18" textAnchor="middle" fontSize="9" fontWeight="bold" fill="white" fontFamily="sans-serif">T</text>
    </svg>
  );
}

export function ExcelIcon({ className = iconBaseClass, ...props }: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true" {...props}>
      <path d="M14 4h5a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-5V4z" fill="#107C41" />
      <path d="M15.5 7h4v2h-4V7zm0 4h4v2h-4v-2zm0 4h4v2h-4v-2z" fill="#21A366" />
      <rect x="3" y="6" width="10" height="12" rx="1.5" fill="#0E5C2F" />
      <text x="8" y="15" textAnchor="middle" fontSize="9" fontWeight="bold" fill="white" fontFamily="sans-serif">X</text>
    </svg>
  );
}

export function GmailIcon({ className = iconBaseClass, ...props }: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true" {...props}>
      <rect x="3" y="5" width="18" height="14" rx="2" fill="#F8F9FA" className="fill-[#F8F9FA] dark:fill-muted" stroke="#EA4335" strokeWidth="1.5" />
      <path d="M3 6.5L12 13L21 6.5" stroke="#EA4335" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 18.5L9.5 13" stroke="#EA4335" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M20 18.5L14.5 13" stroke="#EA4335" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

export function GoogleCalendarIcon({ className = iconBaseClass, ...props }: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true" {...props}>
      <rect x="3" y="4" width="18" height="17" rx="3" fill="#FFFFFF" className="fill-white dark:fill-muted" stroke="#4285F4" strokeWidth="1.5" />
      <path d="M3 9H21" stroke="#4285F4" strokeWidth="1.5" />
      <rect x="7" y="2" width="2" height="3" rx="1" fill="#EA4335" />
      <rect x="15" y="2" width="2" height="3" rx="1" fill="#4285F4" />
      <text x="12" y="17.5" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#4285F4" className="fill-[#4285F4] dark:fill-blue-400" fontFamily="system-ui, sans-serif">31</text>
    </svg>
  );
}

export function GoogleSheetsIcon({ className = iconBaseClass, ...props }: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true" {...props}>
      <path d="M5 3.5C5 2.67 5.67 2 6.5 2H14L19 7V20.5C19 21.33 18.33 22 17.5 22H6.5C5.67 22 5 21.33 5 20.5V3.5Z" fill="#0F9D58" />
      <path d="M14 2V7H19" fill="#87CEAC" />
      <rect x="7.5" y="10" width="9" height="8" rx="0.5" fill="#FFFFFF" fillOpacity="0.3" stroke="#FFFFFF" strokeWidth="0.75" />
      <line x1="7.5" y1="14" x2="16.5" y2="14" stroke="#FFFFFF" strokeWidth="0.75" />
      <line x1="12" y1="10" x2="12" y2="18" stroke="#FFFFFF" strokeWidth="0.75" />
    </svg>
  );
}

export function GoogleDocsIcon({ className = iconBaseClass, ...props }: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true" {...props}>
      <path d="M5 3.5C5 2.67 5.67 2 6.5 2H14L19 7V20.5C19 21.33 18.33 22 17.5 22H6.5C5.67 22 5 21.33 5 20.5V3.5Z" fill="#4285F4" />
      <path d="M14 2V7H19" fill="#A1C2FA" />
      <line x1="8" y1="11" x2="16" y2="11" stroke="#FFFFFF" strokeWidth="1.2" strokeLinecap="round" />
      <line x1="8" y1="14" x2="16" y2="14" stroke="#FFFFFF" strokeWidth="1.2" strokeLinecap="round" />
      <line x1="8" y1="17" x2="13" y2="17" stroke="#FFFFFF" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

export function GoogleSlidesIcon({ className = iconBaseClass, ...props }: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true" {...props}>
      <path d="M5 3.5C5 2.67 5.67 2 6.5 2H14L19 7V20.5C19 21.33 18.33 22 17.5 22H6.5C5.67 22 5 21.33 5 20.5V3.5Z" fill="#F4B400" />
      <path d="M14 2V7H19" fill="#FBE7A1" />
      <rect x="8" y="11" width="8" height="6" rx="0.5" fill="#FFFFFF" fillOpacity="0.4" stroke="#FFFFFF" strokeWidth="0.75" />
    </svg>
  );
}

export function JiraIcon({ className = iconBaseClass, ...props }: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true" {...props}>
      <path d="M11.53 2c-.22 0-.44.09-.6.25L5.7 7.48a.85.85 0 0 0 0 1.2l5.77 5.77c.33.33.87.33 1.2 0l5.23-5.23a.85.85 0 0 0 0-1.2L12.13 2.25a.85.85 0 0 0-.6-.25z" fill="#0052CC" />
      <path d="M11.53 8.5c-.22 0-.44.09-.6.25L7.7 11.98a.85.85 0 0 0 0 1.2l3.77 3.77c.33.33.87.33 1.2 0l3.23-3.23a.85.85 0 0 0 0-1.2L12.13 8.75a.85.85 0 0 0-.6-.25z" fill="#2684FF" />
      <path d="M11.53 15c-.22 0-.44.09-.6.25L9.7 16.48a.85.85 0 0 0 0 1.2l1.77 1.77c.33.33.87.33 1.2 0l1.23-1.23a.85.85 0 0 0 0-1.2L12.13 15.25a.85.85 0 0 0-.6-.25z" fill="#79B8FF" />
    </svg>
  );
}

export function FigmaIcon({ className = iconBaseClass, ...props }: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true" {...props}>
      <path d="M9 2a3 3 0 0 0 0 6h3V2H9z" fill="#F24E1E" />
      <path d="M12 2h3a3 3 0 1 1 0 6h-3V2z" fill="#FF7262" />
      <path d="M9 8a3 3 0 0 0 0 6h3V8H9z" fill="#A259FF" />
      <circle cx="15" cy="11" r="3" fill="#1ABCFE" />
      <path d="M9 14a3 3 0 0 0 0 6 3 3 0 0 0 3-3v-3H9z" fill="#0ACF83" />
    </svg>
  );
}

export interface ConnectorBadgeConfig {
  name: string;
  match: RegExp;
  icon: React.ReactNode;
  className?: string;
}

export const CONNECTOR_BADGE_CLASS =
  'border border-gray-300 bg-gray-100 text-gray-800 hover:bg-gray-200/80 hover:border-gray-400 shadow-xs dark:border-gray-500 dark:bg-muted dark:text-foreground dark:hover:bg-muted/80 dark:hover:border-gray-400';

// Link text that asks the user to connect an account, e.g. "Connect your Outlook account".
export const CONNECT_ACTION_TEXT = /\b(?:connect|reconnect|sign\s*in|log\s*in|authori[sz]e)\b/i;

export const CONNECTOR_BADGE_CONFIGS: ConnectorBadgeConfig[] = [
  {
    name: 'Agent',
    match: /(?:^|\b)(?:agent|bot)\b|chat\?.*agentId=|agentId=/i,
    icon: <Bot className="h-3.5 w-3.5 shrink-0 opacity-80 group-hover:opacity-100 transition-opacity" />,
  },
  {
    name: 'HubSpot',
    match: /hub[\s_-]*spot|hubspot/i,
    icon: <img src={hubspotIcon} alt="HubSpot" className="h-3.5 w-3.5 object-contain shrink-0" aria-hidden="true" />,
  },
  {
    name: 'OneDrive',
    match: /one[\s_-]*drive|onedrive/i,
    icon: <img src={onedriveIcon} alt="OneDrive" className="h-3.5 w-3.5 object-contain shrink-0" aria-hidden="true" />,
  },
  {
    name: 'SharePoint',
    match: /share[\s_-]*point|sharepoint/i,
    icon: <img src={sharepointIcon} alt="SharePoint" className="h-3.5 w-3.5 object-contain shrink-0" aria-hidden="true" />,
  },
  {
    name: 'Outlook',
    match: /outlook/i,
    icon: <img src={outlookIcon} alt="Outlook" className="h-3.5 w-3.5 object-contain shrink-0" aria-hidden="true" />,
  },
  {
    name: 'GitHub',
    match: /git[\s_-]*hub|github/i,
    icon: <img src={githubIcon} alt="GitHub" className="h-3.5 w-3.5 object-contain shrink-0 dark:invert" aria-hidden="true" />,
  },
  {
    name: 'Slack',
    match: /slack/i,
    icon: <SlackIcon />,
  },
  {
    name: 'Microsoft Teams',
    match: /microsoft[\s_-]*teams|ms[\s_-]*teams|\bteams\b|teams\.microsoft\.com/i,
    icon: <TeamsIcon />,
  },
  {
    name: 'Excel',
    match: /excel/i,
    icon: <ExcelIcon />,
  },
  {
    name: 'Gmail',
    match: /gmail|mail\.google\.com/i,
    icon: <GmailIcon />,
  },
  {
    name: 'Google Calendar',
    match: /google[\s_-]*calendar|gcalendar|calendar\.google\.com|\bcalendar\b/i,
    icon: <GoogleCalendarIcon />,
  },
  {
    name: 'Google Sheets',
    match: /google[\s_-]*sheets?|gsheets?|spreadsheets?|docs\.google\.com\/spreadsheets|sheets\.google\.com/i,
    icon: <GoogleSheetsIcon />,
  },
  {
    name: 'Google Slides',
    match: /google[\s_-]*(?:slides?|presentations?)|gslides?|docs\.google\.com\/presentation|slides\.google\.com/i,
    icon: <GoogleSlidesIcon />,
  },
  {
    name: 'Google Docs',
    match: /google[\s_-]*(?:docs?|documents?)|gdocs?|docs\.google\.com/i,
    icon: <GoogleDocsIcon />,
  },
  {
    name: 'Google Drive',
    match: /google[\s_-]*drive|gdrive|drive\.google\.com/i,
    icon: <img src={gdriveIcon} alt="Google Drive" className="h-3.5 w-3.5 object-contain shrink-0" aria-hidden="true" />,
  },
  {
    name: 'Jira',
    match: /jira|atlassian\.net/i,
    icon: <JiraIcon />,
  },
  {
    name: 'Figma',
    match: /figma/i,
    icon: <FigmaIcon />,
  },
];

export function getConnectorBadgeInfo(text: string, href?: string): ConnectorBadgeConfig | null {
  const targetText = text || '';
  const targetHref = typeof href === 'string' ? href : '';

  for (const config of CONNECTOR_BADGE_CONFIGS) {
    if (config.match.test(targetText) || (targetHref && config.match.test(targetHref))) {
      return {
        ...config,
        className: config.className || CONNECTOR_BADGE_CLASS,
      };
    }
  }

  return null;
}
